import { useState, useMemo, useEffect, useCallback } from "react";
import { countBlanks, isAllBlanksFilled } from "../../../utils/quiz-parser.js";
function useFillBlankQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange
}) {
  const [startTime] = useState(() => Date.now());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false);
  const blankCount = useMemo(() => countBlanks(quiz.problem), [quiz.problem]);
  const isSolved = solveResult !== null && solveResult !== void 0;
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers;
  const computedInitialAnswers = useMemo(() => {
    if (initialAnswersProp && initialAnswersProp.length > 0) {
      return initialAnswersProp;
    }
    if (isViewSolved && (solveResult == null ? void 0 : solveResult.correctAnswers)) {
      return solveResult.correctAnswers;
    }
    return new Array(blankCount).fill("");
  }, [blankCount, isViewSolved, solveResult == null ? void 0 : solveResult.correctAnswers, initialAnswersProp]);
  const [answers, setAnswers] = useState(computedInitialAnswers);
  const canSubmit = isAllBlanksFilled(answers);
  useEffect(() => {
    onAnswerChange == null ? void 0 : onAnswerChange(answers);
  }, [answers, onAnswerChange]);
  const blankKeys = useMemo(() => {
    const keysMap = /* @__PURE__ */ new Map();
    const answerReg = /\{\{([0-9]+)?}}/gim;
    let match;
    let blankIndex = 0;
    while ((match = answerReg.exec(quiz.problem)) !== null) {
      const key = match[1] || "";
      keysMap.set(blankIndex, key);
      blankIndex++;
    }
    return keysMap;
  }, [quiz.problem]);
  const handleBlankClick = useCallback((index) => {
    if (isSolved || isViewSolved) return;
    setCurrentIndex(index);
  }, [isSolved, isViewSolved]);
  const handleInputChange = useCallback((index, value, key) => {
    if (isSolved || isViewSolved) return;
    setAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[index] = value;
      if (key && key !== "") {
        blankKeys.forEach((blankKey, blankIndex) => {
          if (blankKey === key && blankIndex !== index) {
            newAnswers[blankIndex] = value;
          }
        });
      }
      return newAnswers;
    });
  }, [isSolved, isViewSolved, blankKeys]);
  const handleKeypadInput = useCallback((value) => {
    if (isSolved || isViewSolved) return;
    const currentKey = blankKeys.get(currentIndex) || "";
    setAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[currentIndex] = value;
      if (currentKey && currentKey !== "") {
        blankKeys.forEach((blankKey, blankIndex) => {
          if (blankKey === currentKey && blankIndex !== currentIndex) {
            newAnswers[blankIndex] = value;
          }
        });
      }
      return newAnswers;
    });
    if (currentIndex < blankCount - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, blankCount, isSolved, isViewSolved, blankKeys]);
  const handleKeypadClear = useCallback(() => {
    if (isSolved || isViewSolved) return;
    const currentKey = blankKeys.get(currentIndex) || "";
    setAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[currentIndex] = "";
      if (currentKey && currentKey !== "") {
        blankKeys.forEach((blankKey, blankIndex) => {
          if (blankKey === currentKey && blankIndex !== currentIndex) {
            newAnswers[blankIndex] = "";
          }
        });
      }
      return newAnswers;
    });
  }, [currentIndex, isSolved, isViewSolved, blankKeys]);
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return;
    const elapsedTime = Date.now() - startTime;
    onSolve == null ? void 0 : onSolve(quizId, answers, quiz.vars, elapsedTime);
  }, [answers, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime]);
  return {
    answers,
    currentIndex,
    blankCount,
    blankKeys,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleBlankClick,
    handleInputChange,
    handleKeypadInput,
    handleKeypadClear,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved
  };
}
export {
  useFillBlankQuiz
};
//# sourceMappingURL=use-fill-blank-quiz.js.map
